// Pure module without aliased imports: also imported by `bootstrapSSR` and by the SSR middleware
// (through `coreUtils/cms/contentPageCmsQuery`).
import type {TCmsQueryVariables} from '../../classes/content/providers/cmsQueryTypes';

export type CmsQueryKeyOptions = {
	contentType: string;
	language: string;
	siteId?: string;
	providerId?: string;
	deps?: readonly unknown[];
	config?: unknown;
};

/** `config` of `useCmsContent`: variables of the primary query and of the fallback queries. */
export type CmsQueryConfig = {
	variables?: TCmsQueryVariables;
	fallbackVariables?: TCmsQueryVariables[];
};

/**
 * Builds a deterministic React Query cache key for CMS content.
 * Used by both SSR (`bootstrapSSR`) and CSR (`useCmsContent`) to guarantee key alignment.
 */
export function buildCmsQueryKey({contentType, language, providerId, siteId, deps = [], config}: CmsQueryKeyOptions): readonly unknown[] {
	const key: unknown[] = ['cms-content', contentType, language];

	if (providerId) {
		key.push(providerId);
	}

	if (siteId) {
		key.push(siteId);
	}

	key.push(...deps);

	if (config) {
		key.push(config);
	}

	return key;
}

/**
 * `filterSite` of `useCmsContent`: adds `fields.sites` (`[siteId, 'all']`, or `['all']` without a site) to the
 * variables and to every fallback.
 */
export function applyCmsSiteFilter<T extends CmsQueryConfig | undefined>(config: T, siteId?: string): T {
	const sites = siteId ? [siteId, 'all'] : ['all'];
	const existingFields = config?.variables?.fields as Record<string, unknown> | undefined;

	return {
		...config,
		variables: {
			...config?.variables,
			fields: {
				...existingFields,
				sites,
			},
		},
		...(config?.fallbackVariables && {
			fallbackVariables: config.fallbackVariables.map((fv) => {
				const fvFields = fv.fields as Record<string, unknown> | undefined;

				return {...fv, fields: {...fvFields, sites}};
			}),
		}),
	} as T;
}

/**
 * React Query key of `useCmsContent` for the given (effective, site filter applied) `config`: the language is
 * left out when the query asks all the locales (`variables.allLocales`).
 */
export function buildCmsContentQueryKey({
	contentType,
	providerId,
	config,
	currentLanguage,
	deps = [],
}: {
	contentType: string;
	providerId: string;
	config: CmsQueryConfig | undefined;
	currentLanguage: string | null;
	deps?: readonly unknown[];
}): readonly unknown[] {
	return buildCmsQueryKey({
		contentType,
		providerId,
		language: config?.variables?.allLocales ? null : currentLanguage,
		deps,
		config,
	});
}
